import type {
  CreateAssistantTaskInput,
  CreateAssistantTaskOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  createAssistantTaskInputSchema,
  createAssistantTaskOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import { AiToolkitAssistantNotFoundError } from "@/modules/ai-toolkit/errors";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import { uuidModule } from "@/modules/uuid";
import { getCurrentAssistantId } from "@/store/assistant/assistant-id";
import { SubmitAssistantChangeRequestsOperation } from "@/store/sync/operations/assistant-change-requests/SubmitAssistantChangeRequestsOperation";
import type { ISubmitAssistantFollowUpChangeRequestPayload } from "@/store/sync/operations/types";
import type { AppStore } from "@/store/AppStore";

const buildTaskChangedFields = ({ input }: { input: CreateAssistantTaskInput }): string[] => [
  "title",
  ...(input.description !== undefined ? ["description"] : []),
  ...(input.status !== undefined ? ["status"] : []),
  ...(input.dueAt !== undefined ? ["due_at"] : []),
  ...(input.projectId !== undefined ? ["project_id"] : []),
];

const buildFollowUpChangedFields = ({
  input,
  projectId,
}: {
  input: NonNullable<CreateAssistantTaskInput["followUp"]>;
  projectId: CreateAssistantTaskInput["projectId"];
}): string[] => [
  "content",
  "scheduled_for",
  "task_id",
  ...(projectId !== undefined ? ["project_id"] : []),
  ...(input.triggerType !== undefined ? ["trigger_type"] : []),
  ...(input.commitmentKind !== undefined ? ["commitment_kind"] : []),
  ...(input.dueAt !== undefined ? ["due_at"] : []),
  ...(input.deadlineAt !== undefined ? ["deadline_at"] : []),
  ...(input.triggerNotBefore !== undefined ? ["trigger_not_before"] : []),
  ...(input.triggerNotAfter !== undefined ? ["trigger_not_after"] : []),
];

export const createAssistantTask = async ({
  store,
  input,
}: {
  store: AppStore;
  input: CreateAssistantTaskInput;
}): Promise<CreateAssistantTaskOutput> => {
  const assistantId = await getCurrentAssistantId({ store });

  if (!assistantId) {
    throw new AiToolkitAssistantNotFoundError();
  }

  const taskId = uuidModule.generate();
  const followUpId = input.followUp ? uuidModule.generate() : undefined;
  const requestGroupId = uuidModule.generate();
  const followUpChangeRequests: ISubmitAssistantFollowUpChangeRequestPayload[] | undefined =
    input.followUp && followUpId
      ? [
          {
            id: followUpId,
            change_kind: "create",
            requested_content: input.followUp.content,
            requested_scheduled_for: input.followUp.scheduledFor,
            requested_task_id: taskId,
            requested_project_id: input.projectId ?? null,
            requested_trigger_type: input.followUp.triggerType ?? null,
            requested_commitment_kind: input.followUp.commitmentKind ?? null,
            requested_due_at: input.followUp.dueAt ?? null,
            requested_deadline_at: input.followUp.deadlineAt ?? null,
            requested_trigger_not_before: input.followUp.triggerNotBefore ?? null,
            requested_trigger_not_after: input.followUp.triggerNotAfter ?? null,
            changed_fields: buildFollowUpChangedFields({
              input: input.followUp,
              projectId: input.projectId,
            }),
          },
        ]
      : undefined;

  await new SubmitAssistantChangeRequestsOperation({
    store,
    payload: {
      request_group_id: requestGroupId,
      assistant_id: assistantId,
      task_change_requests: [
        {
          id: taskId,
          change_kind: "create",
          requested_title: input.title,
          requested_description: input.description ?? null,
          requested_status: input.status ?? null,
          requested_due_at: input.dueAt ?? null,
          requested_project_id: input.projectId ?? null,
          changed_fields: buildTaskChangedFields({ input }),
        },
      ],
      follow_up_change_requests: followUpChangeRequests,
    },
  }).execute();

  return {
    submitted: true,
    taskId,
    ...(followUpId ? { followUpId } : {}),
    requestGroupId,
  };
};

export const createAssistantTaskTool: AiToolkitToolDefinition<
  AiToolkitToolName.CreateAssistantTask,
  CreateAssistantTaskInput,
  CreateAssistantTaskOutput
> = {
  name: AiToolkitToolName.CreateAssistantTask,
  inputSchema: createAssistantTaskInputSchema,
  outputSchema: createAssistantTaskOutputSchema,
  strict: true,
  execute: createAssistantTask,
};
